/* ==========================================================================
   PAMPER & PEACE — cart-drawer.js
   Open/close, AJAX add / qty update / remove, gift-message note, free-gift
   progress, and same-scent-family pairings. Updates are done with the Shopify
   Section Rendering API so the drawer re-renders server-side (prices, discounts,
   gift progress) without a full reload.
   ========================================================================== */
(function () {
  'use strict';
  const Pamper = (window.Pamper = window.Pamper || {});
  const routes = Pamper.routes || {};
  const SECTION = 'cart-drawer';

  const body = document.body;
  const drawer = document.querySelector('[data-cart-drawer]');
  const overlay = document.querySelector('[data-cart-overlay]');
  let releaseTrap;
  let lastFocused;

  /* ---------------- open / close ---------------- */
  function openDrawer() {
    if (!drawer) {
      window.location.href = routes.cart_url;
      return;
    }
    lastFocused = document.activeElement;
    body.dataset.cartOpen = 'true';
    drawer.setAttribute('aria-hidden', 'false');
    if (Pamper.utils) releaseTrap = Pamper.utils.trapFocus(drawer);
    const closeBtn = drawer.querySelector('[data-cart-close]');
    requestAnimationFrame(() => closeBtn && closeBtn.focus());
  }
  function closeDrawer() {
    body.dataset.cartOpen = 'false';
    if (drawer) drawer.setAttribute('aria-hidden', 'true');
    if (releaseTrap) releaseTrap();
    if (lastFocused) lastFocused.focus();
  }
  Pamper.openCart = openDrawer;
  Pamper.closeCart = closeDrawer;

  document.querySelectorAll('[data-cart-open-trigger]').forEach((t) =>
    t.addEventListener('click', (e) => {
      if (Pamper.settings && Pamper.settings.cartType === 'page') return; // let it navigate
      e.preventDefault();
      openDrawer();
    })
  );
  if (overlay) overlay.addEventListener('click', closeDrawer);
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) closeDrawer();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && body.dataset.cartOpen === 'true') closeDrawer();
  });

  /* ---------------- rendering helpers ---------------- */
  function setBusy(state) {
    if (drawer) drawer.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  function updateHeaderCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      el.textContent = `(${count})`;
    });
  }

  function renderFromSection(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-cart-contents]');
    const current = drawer && drawer.querySelector('[data-cart-contents]');
    if (fresh && current) current.replaceWith(fresh);
    // Echo the active scent universe onto the drawer accent.
    if (Pamper.activeTheme && Pamper.activeTheme['--pp-primary'] && current) {
      drawer.querySelector('[data-cart-contents]').style.setProperty(
        '--drawer-accent',
        Pamper.activeTheme['--pp-primary']
      );
    }
    // Pull item count from the re-rendered label if available.
    const label = parsed.querySelector('[data-cart-count-label]');
    if (label) {
      const n = parseInt(label.textContent.replace(/\D/g, ''), 10);
      if (!Number.isNaN(n)) updateHeaderCount(n);
    }
    hydratePairings();
  }

  async function fetchSection() {
    const res = await fetch(`${routes.cart_url}?section_id=${SECTION}`);
    return res.text();
  }

  /* ---------------- mutations ---------------- */
  async function addToCart(form, opener) {
    const btn = form.querySelector('[data-add-to-cart]');
    const original = btn ? btn.innerHTML : '';
    if (btn) {
      btn.setAttribute('aria-disabled', 'true');
      btn.innerHTML = (Pamper.strings && Pamper.strings.adding) || 'Adding…';
    }
    try {
      const formData = new FormData(form);
      formData.append('sections', SECTION);
      formData.append('sections_url', routes.cart_url);
      const res = await fetch(routes.cart_add, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await res.json();
      if (data.status) {
        // 422 etc — surface Shopify's message.
        Pamper.toast(data.description || data.message || 'Could not add to bag');
        return;
      }
      if (data.sections && data.sections[SECTION]) {
        renderFromSection(data.sections[SECTION]);
      } else {
        renderFromSection(await fetchSection());
      }
      Pamper.toast((Pamper.strings && Pamper.strings.addedToCart) || 'Added ✨');
      openDrawer();
    } catch (err) {
      Pamper.toast('Something went wrong — try again');
    } finally {
      if (btn) {
        btn.removeAttribute('aria-disabled');
        btn.innerHTML = original;
      }
    }
  }

  async function changeLine(key, quantity) {
    setBusy(true);
    try {
      const res = await fetch(routes.cart_change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: SECTION, sections_url: routes.cart_url }),
      });
      const cart = await res.json();
      updateHeaderCount(cart.item_count);
      if (cart.sections && cart.sections[SECTION]) renderFromSection(cart.sections[SECTION]);
      else renderFromSection(await fetchSection());
    } catch (err) {
      Pamper.toast('Could not update bag');
    } finally {
      setBusy(false);
    }
  }

  let pendingNote = null;

  function postNote(note) {
    pendingNote = null;
    return fetch(`${routes.cart_url}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    });
  }

  const saveNote = Pamper.utils
    ? Pamper.utils.debounce((note) => postNote(note), 600)
    : function () {};

  // Flush an un-saved note before leaving for checkout, so the gift message
  // isn't lost when the shopper types and clicks straight through.
  async function flushNote() {
    if (pendingNote === null) return;
    try {
      await postNote(pendingNote);
    } catch (err) {
      /* checkout still proceeds */
    }
  }

  /* ---------------- delegated events inside the drawer ---------------- */
  if (drawer) {
    drawer.addEventListener('click', (e) => {
      const up = e.target.closest('[data-qty-up]');
      const down = e.target.closest('[data-qty-down]');
      const remove = e.target.closest('[data-remove-item]');
      if (up || down) {
        const ctrl = up || down;
        const input = drawer.querySelector(`[data-qty-input][data-line-key="${ctrl.dataset.lineKey}"]`);
        let q = parseInt(input.value, 10) || 0;
        q = up ? q + 1 : Math.max(0, q - 1);
        changeLine(ctrl.dataset.lineKey, q);
      }
      if (remove) {
        e.preventDefault();
        changeLine(remove.dataset.lineKey, 0);
      }
    });
    drawer.addEventListener('change', (e) => {
      const input = e.target.closest('[data-qty-input]');
      if (input) changeLine(input.dataset.lineKey, Math.max(0, parseInt(input.value, 10) || 0));
    });
    drawer.addEventListener('input', (e) => {
      const note = e.target.closest('[data-cart-note]');
      if (note) {
        pendingNote = note.value;
        saveNote(note.value);
      }
    });
    drawer.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-checkout-form]');
      if (!form) return;
      if (pendingNote === null) return; // native submit -> straight to /checkout
      e.preventDefault();
      setBusy(true);
      await flushNote();
      form.submit();
    });
  }

  /* ---------------- add-to-cart form interception (cards, PDP, pairings) ---------------- */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-quick-add-form], [data-product-form]');
    if (!form) return;
    // Let the dynamic checkout (Shop Pay / Buy now) submit go straight to checkout.
    const submitter = e.submitter;
    if (submitter && submitter.closest('.shopify-payment-button, [data-shopify="payment-button"]')) return;
    e.preventDefault();
    addToCart(form, document.activeElement);
  });

  /* ---------------- pairings: same scent_family via Recommendations API ---------------- */
  async function hydratePairings() {
    const slot = drawer && drawer.querySelector('[data-cart-pairings][data-recommend-for]');
    if (!slot) return;
    const id = slot.dataset.recommendFor;
    if (!id) return;
    try {
      const res = await fetch(
        `/recommendations/products?product_id=${id}&limit=2&intent=related&section_id=${SECTION}`
      );
      // Fallback to JSON recommendations if section rendering isn't wired.
      const json = await fetch(`/recommendations/products.json?product_id=${id}&limit=2&intent=related`).then(
        (r) => r.json()
      );
      const products = (json.products || []).slice(0, 2);
      if (!products.length) return;
      slot.hidden = false;
      slot.innerHTML =
        `<h3 class="eyebrow" style="font-size:1.1rem">${
          (Pamper.strings && Pamper.strings.pairingsTitle) || 'You might also love'
        }</h3>` +
        products
          .map(
            (p) => `
        <div class="cart-pairing">
          <img src="${p.featured_image || ''}" loading="lazy" width="48" height="48" alt="" onerror="this.style.display='none'">
          <div>
            <p style="font-weight:600;font-size:var(--pp-fs-300)">${p.title}</p>
            <p class="text-soft" style="font-size:var(--pp-fs-300)">${Pamper.utils.money(p.price)}</p>
          </div>
          <form data-quick-add-form method="post" action="${routes.cart_add}">
            <input type="hidden" name="id" value="${p.variants[0].id}">
            <button class="btn btn--ghost" type="submit" data-add-to-cart>Add</button>
          </form>
        </div>`
          )
          .join('');
    } catch (err) {
      /* non-critical */
    }
  }

  // Auto-open the drawer if redirected back with ?cart=open
  if (new URLSearchParams(location.search).get('cart') === 'open') openDrawer();
  hydratePairings();
})();
